import {
  Product,
  ProductCode,
  ProductFeature,
  ProductOfferingBenefit,
  ProductOfferingPlatform,
  ProductPlatformGroup,
  ProductPlatformOther,
  PublicationProduct,
  productInformationRegistry,
} from '@mkp/shared/models';
import {
  getBenefitsFromFeatures,
  getFeaturesFromProducts,
  orderFeatures,
  removeApprenticeshipProducts,
} from './product-offering.helper';
import {
  formatPrice,
  isCalendarProductFeature,
  isLiteProduct,
  isPlatformFeature,
} from './product.helper';

export function toPublicationProducts(
  products: Product[] | undefined
): PublicationProduct[] | undefined {
  // exclude apprenticeship products
  products = products ? removeApprenticeshipProducts(products) : undefined;
  return products?.map((product) => toPublicationProduct(product));
}

export function toPublicationProduct(product: Product): PublicationProduct {
  return {
    ...product,
    periodPrice: formatPrice(product.basePrice),
    benefits: getBenefitsTranslations(product.features),
  };
}

/**
 * Lite products surface a tooltip naming the platform their 7 days run on.
 * The platform differs per brand, so JobUp and Jobs resolve to distinct keys.
 * @returns Tooltip translation key for lite products, undefined for other products
 */
export function getLiteProductTooltip(productCode: ProductCode): string | undefined {
  if (!isLiteProduct(productCode)) {
    return undefined;
  }

  return productCode === ProductCode.JOBUP24LITE
    ? 'PRODUCT_OFFERING.JOBUP.FEATURE_ONLINE.MODAL.DESCRIPTION'
    : 'PRODUCT_OFFERING.LITE.CALENDAR_DAYS.TOOLTIP';
}

/**
 * Converts an offline ProductCode to a minimal Product object
 * These products are not fetched from the ecom API but are defined in productInformationRegistry
 * Only includes attributes required for rendering and price calculation
 * @param productCode The offline product code to convert
 * @returns Minimal Product object for offline products
 */
export function toProduct(productCode: ProductCode): Product {
  const productInfo =
    productInformationRegistry[productCode as keyof typeof productInformationRegistry];
  const price = { current: 0, currency: 'CHF' };
  const variantCode = `${productCode}-variant-0` as const;

  return {
    code: productCode,
    name: productInfo.name.en,
    slug: productCode.toLowerCase(),
    variants: {
      [variantCode]: {
        code: variantCode,
        name: productInfo.name.en,
        originalPrice: price,
        price: price,
        prices: { 1: price },
      },
    },
    features: [],
    calendar: {
      id: ProductOfferingBenefit.Calendar,
      translation: `PRODUCT.FEATURE.CALENDAR.DAYS_${productInfo.publicationDays}`,
      period: productInfo.publicationDays,
    },
    longname: productInfo.name.en,
    currency: 'CHF',
    originalPrice: price,
    price: price,
  } as unknown as Product;
}

export function getPlatformIds(productFeatures: ProductFeature[]): ProductOfferingPlatform[] {
  return productFeatures.filter(isPlatformFeature).map((feature) => feature.id);
}

export function getProductOfferingBenefits(product: Product): ProductOfferingBenefit[] {
  const features = orderFeatures(getFeaturesFromProducts([product]));
  return getBenefitsFromFeatures(features);
}

function getBenefitsTranslations(productFeatures: ProductFeature[]): string[] {
  const benefitsWithoutCalendarAndPlatform = productFeatures
    .filter((feature) => !isCalendarProductFeature(feature) && !isPlatformFeature(feature))
    .map((feature) => feature.title);
  const platformBenefit = getPlatformBenefitTranslation(productFeatures);

  return [platformBenefit, ...(benefitsWithoutCalendarAndPlatform as string[])];
}

function getPlatformBenefitTranslation(productFeatures: ProductFeature[]): string {
  const platforms = getPlatformIds(productFeatures);
  const isJobs = platforms.includes(ProductPlatformGroup.Jobs);
  const isJs24 = platforms.includes(ProductPlatformGroup.JobScout24);
  const isJobUp = platforms.includes(ProductPlatformGroup.JobUp);
  const isJobWinner = platforms.includes(ProductPlatformOther.JobWinner);

  if (isJobs && isJs24 && isJobWinner && isJobUp) {
    return 'PRODUCT.BENEFIT.VISIBILITY_JOBS_JS24_AND_JOB_WINNER';
  }

  if (isJobs && isJs24) {
    return 'PRODUCT.BENEFIT.VISIBILITY_JOBS_AND_JS24';
  }

  if (isJobs) {
    return 'PRODUCT.BENEFIT.VISIBILITY_JOBS';
  }

  if (isJs24) {
    return 'PRODUCT.BENEFIT.VISIBILITY_JS24';
  }

  if (isJobUp) {
    return 'PRODUCT.BENEFIT.VISIBILITY_JOBUP';
  }

  return '';
}
